import { memo, useMemo } from "react";
import s from "./CurrentEntries.module.scss";
import { IonLabel } from "@ionic/react";
import { useWebUserStore } from "../../../../CommonStores";
import { shallow } from "zustand/shallow";
import {
  formatWordPluralize,
  useBreakpoint,
} from "../../../../../Common/utils/common";
import cx from "classnames";

import fixbillingLocker from "../../../../assets/images/fixbilling_locker.png";
import useWLPromoStore from "../../../../components/WLPromoField/store";
import { usePurchaseV2Store } from "../store";
import Stripe from "stripe";

const CurrentEntries = () => {
  const [user, userEntries, latestFailedStripePaymentAttempt] = useWebUserStore(
    (store) => [
      store.user,
      store.user?.entryPoints,
      store.latestFailedStripePaymentAttempt,
    ],
    shallow
  );
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  const { selectedProduct } = usePurchaseV2Store();
  const { validWLPromo } = useWLPromoStore();

  const renderMainTitle = useMemo(() => {
    const attemptCount = latestFailedStripePaymentAttempt
      ? (latestFailedStripePaymentAttempt?.data.object as Stripe.Invoice)
          .attempt_count || 0
      : 0;

    switch (attemptCount) {
      case 2:
        return (
          <>
            <IonLabel className={s.mainTitle}>
              {user?.firstName?.toUpperCase() || "-"}, YOU'RE ABOUT TO
            </IonLabel>
            <IonLabel className={s.urgentText}>LOSE YOUR SPOT</IonLabel>
          </>
        );
      case 3:
        return (
          <>
            <IonLabel className={s.mainTitle}>
              YOUR GIVEAWAY ENTRIES WILL BE
            </IonLabel>
            <div style={{ display: "flex", gap: 8 }}>
              <IonLabel className={s.urgentText}>Gone</IonLabel>
              <IonLabel className={s.mainTitle}>
                {user?.firstName?.toUpperCase() || "-"}
              </IonLabel>
            </div>
          </>
        );
      case 4:
        return (
          <>
            <IonLabel className={s.urgentText}>YOU'RE OUT</IonLabel>
            <IonLabel className={s.mainTitle}>
              IF YOU DON'T TAKE ACTION NOW
              {` ${user?.firstName?.toUpperCase() || "-"}`}
            </IonLabel>
          </>
        );
      case 5:
        return (
          <>
            <IonLabel className={s.mainTitle}>
              {user?.firstName?.toUpperCase() || "-"}, LETTING GO NOW MEANS
            </IonLabel>
            <IonLabel className={s.urgentText}>LOSING IT ALL</IonLabel>
          </>
        );
      case 6:
        return (
          <>
            <div style={{ display: "flex", gap: 12 }}>
              <IonLabel className={s.mainTitle}>THIS YOUR</IonLabel>
              <IonLabel className={s.urgentText}>RESET</IonLabel>
              <IonLabel className={s.mainTitle}>
                {user?.firstName?.toUpperCase() || "-"}
              </IonLabel>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <IonLabel className={s.mainTitle}>IF YOU DON'T</IonLabel>
              <IonLabel className={s.urgentText}>ACT NOW</IonLabel>
            </div>
          </>
        );
      case 7:
        return (
          <>
            <div style={{ display: "flex", gap: 12 }}>
              <IonLabel className={s.mainTitle}>THIS IS YOUR</IonLabel>
              <IonLabel className={s.urgentText}>FINAL WARNING</IonLabel>
            </div>
            <IonLabel className={s.mainTitle}>
              {user?.firstName?.toUpperCase() || "-"} FIX YOUR PAYMENT
            </IonLabel>
          </>
        );
      default:
        return (
          <>
            <IonLabel className={s.mainTitle}>
              DO <span className={s.urgentText}>NOTHING</span>, WIN{" "}
              <span className={s.urgentText}>NOTHING</span>
              <br />
              {user?.firstName?.toUpperCase() || "-"}
            </IonLabel>
          </>
        );
    }
  }, [user, latestFailedStripePaymentAttempt]);

  const renderPromoSpiel = useMemo(() => {
    // Handle promo combinations based on current design (A)
    if (selectedProduct && validWLPromo) {
      const hasStripeCreditDiscount =
        validWLPromo.stripeCreditDiscount !== undefined &&
        validWLPromo.stripeCreditDiscount > 0;
      const isStripeCreditPercentOff = validWLPromo.isStripeCreditPercentOff;
      const hasRewardPoints =
        validWLPromo.rewardPoints && validWLPromo.rewardPoints > 0;
      const hasEntryPoints =
        validWLPromo.entryPoints && validWLPromo.entryPoints > 0;
      const hasFixedDiscount =
        hasStripeCreditDiscount && !isStripeCreditPercentOff;
      const hasPercentageDiscount =
        hasStripeCreditDiscount && isStripeCreditPercentOff;

      // Case A1: No bonuses
      if (!hasStripeCreditDiscount && !hasRewardPoints && !hasEntryPoints) {
        return null; // No display to show
      }

      // Case A2: Percentage Discount only
      if (
        !validWLPromo.isMultiplier &&
        hasPercentageDiscount &&
        !hasRewardPoints &&
        !hasEntryPoints
      ) {
        return (
          <>
            <IonLabel className={s.bonusText}>
              TO ADD A BIT OF SAUCE, WE'LL MAKE IT
              <span className={s.pointsHighlight}>
                {" "}
                {validWLPromo.stripeCreditDiscount}% off
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU FIX YOUR PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case A3: Fixed amount discount only
      if (
        !validWLPromo.isMultiplier &&
        hasFixedDiscount &&
        !hasRewardPoints &&
        !hasEntryPoints
      ) {
        const discountAmount = validWLPromo.stripeCreditDiscount || 0;

        const finalPrice = Math.max(
          1,
          (selectedProduct.price || 0) - discountAmount
        );

        return (
          <>
            <IonLabel className={s.bonusText}>
              MAKE PAYMENT FOR{" "}
              <span className={s.pointsHighlight}>${finalPrice}</span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU FIX YOUR PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case A4: Fixed amount discount + locker points
      if (
        !validWLPromo.isMultiplier &&
        hasFixedDiscount &&
        hasRewardPoints &&
        !hasEntryPoints
      ) {
        const discountAmount = validWLPromo.stripeCreditDiscount || 0;

        const finalPrice = Math.max(
          1,
          (selectedProduct.price || 0) - discountAmount
        );

        return (
          <>
            <IonLabel className={s.bonusText}>
              MAKE PAYMENT FOR{" "}
              <span className={s.pointsHighlight}>${finalPrice} </span> and
              WE'LL THROW in{" "}
              <span className={s.pointsHighlight}>
                {validWLPromo.rewardPoints}{" "}
                {formatWordPluralize("Locker Point", validWLPromo.rewardPoints)}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU FIX YOUR PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case A5: Fixed amount discount + entry points
      if (
        !validWLPromo.isMultiplier &&
        hasFixedDiscount &&
        hasEntryPoints &&
        !hasRewardPoints
      ) {
        const discountAmount = validWLPromo.stripeCreditDiscount || 0;

        const finalPrice = Math.max(
          1,
          (selectedProduct.price || 0) - discountAmount
        );

        return (
          <>
            <IonLabel className={s.bonusText}>
              MAKE PAYMENT FOR{" "}
              <span className={s.pointsHighlight}>${finalPrice} </span>
              and <br />
              WE'LL THROW in{" "}
              <span className={s.pointsHighlight}>
                {validWLPromo.entryPoints}{" "}
                {formatWordPluralize("Entry", validWLPromo.entryPoints)}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU FIX YOUR PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case A6: Locker points only
      if (
        !validWLPromo.isMultiplier &&
        !hasStripeCreditDiscount &&
        hasRewardPoints &&
        !hasEntryPoints
      ) {
        return (
          <>
            <IonLabel className={s.bonusText}>
              TO SWEETEN THE DEAL, WE'LL THROW IN <br />{" "}
              <span className={s.pointsHighlight}>
                {validWLPromo.rewardPoints}{" "}
                {formatWordPluralize("Point", validWLPromo.rewardPoints)}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU COMPLETE PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case A7: Fixed amount discount + BOTH locker points AND entry points
      if (
        !validWLPromo.isMultiplier &&
        hasFixedDiscount &&
        hasRewardPoints &&
        hasEntryPoints
      ) {
        const discountAmount = validWLPromo.stripeCreditDiscount || 0;
        const finalPrice = Math.max(
          1,
          (selectedProduct.price || 0) - discountAmount
        );

        return (
          <>
            <IonLabel className={s.bonusText}>
              MAKE PAYMENT FOR{" "}
              <span className={s.pointsHighlight}>${finalPrice} </span>
              and WE'LL THROW in{" "}
              <span className={s.pointsHighlight}>
                <br />
                {validWLPromo.rewardPoints}{" "}
                {formatWordPluralize("Locker Point", validWLPromo.rewardPoints)}{" "}
                + {validWLPromo.entryPoints}{" "}
                {formatWordPluralize("Entry", validWLPromo.entryPoints)}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU FIX YOUR PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Handle cases we need to handle on our own (B)
      // Case B1: Percentage discount + locker points
      if (
        !validWLPromo.isMultiplier &&
        hasPercentageDiscount &&
        hasRewardPoints &&
        !hasEntryPoints
      ) {
        return (
          <>
            <IonLabel className={s.bonusText}>
              GET{" "}
              <span className={s.pointsHighlight}>
                ${validWLPromo.stripeCreditDiscount}% OFF +{" "}
                {validWLPromo.rewardPoints}{" "}
                {formatWordPluralize("Locker Point", validWLPromo.rewardPoints)}{" "}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU COMPLETE PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case B2: Percentage discount + entry points
      if (
        !validWLPromo.isMultiplier &&
        hasPercentageDiscount &&
        hasEntryPoints &&
        !hasRewardPoints
      ) {
        return (
          <>
            <IonLabel className={s.bonusText}>
              GET{" "}
              <span className={s.pointsHighlight}>
                ${validWLPromo.stripeCreditDiscount}% OFF +{" "}
                {validWLPromo.entryPoints}{" "}
                {formatWordPluralize("Bonus Entry", validWLPromo.entryPoints)}{" "}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU COMPLETE PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case B3: Entries only
      if (
        !validWLPromo.isMultiplier &&
        !hasStripeCreditDiscount &&
        hasEntryPoints &&
        !hasRewardPoints
      ) {
        return (
          <>
            <IonLabel className={s.bonusText}>
              TO SWEETEN THE DEAL, WE'LL THROW IN{" "}
              <span className={s.pointsHighlight}>
                {validWLPromo.entryPoints}{" "}
                {formatWordPluralize("Bonus Entry", validWLPromo.entryPoints)}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU COMPLETE PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case B4: Locker + entries
      if (
        !validWLPromo.isMultiplier &&
        !hasStripeCreditDiscount &&
        hasRewardPoints &&
        hasEntryPoints
      ) {
        return (
          <>
            <IonLabel className={s.bonusText}>
              TO SWEETEN THE DEAL, WE'LL THROW IN{" "}
              <span className={s.pointsHighlight}>
                {validWLPromo.rewardPoints}{" "}
                {formatWordPluralize("Point", validWLPromo.rewardPoints)} +{" "}
                {validWLPromo.entryPoints}{" "}
                {formatWordPluralize("Entry", validWLPromo.entryPoints)}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU COMPLETE PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Case B5: Percentage discount + BOTH locker points AND entry points
      if (
        !validWLPromo.isMultiplier &&
        hasPercentageDiscount &&
        hasRewardPoints &&
        hasEntryPoints
      ) {
        return (
          <>
            <IonLabel className={s.bonusText}>
              GET{" "}
              <span className={s.pointsHighlight}>
                {validWLPromo.stripeCreditDiscount}% OFF +{" "}
                {validWLPromo.rewardPoints}{" "}
                {formatWordPluralize("Locker Point", validWLPromo.rewardPoints)}{" "}
                + {validWLPromo.entryPoints}{" "}
                {formatWordPluralize("Bonus Entry", validWLPromo.entryPoints)}
              </span>
            </IonLabel>
            <IonLabel className={s.completeNow}>
              IF YOU COMPLETE PAYMENT NOW
            </IonLabel>
          </>
        );
      }

      // Handle month's worth (multiplier) cases (C)
      if (validWLPromo.isMultiplier) {
        const hasMultiplierEntryPoints =
          validWLPromo.entryPoints && validWLPromo.entryPoints > 0;
        const hasMultiplierRewardPoints =
          validWLPromo.rewardPoints && validWLPromo.rewardPoints > 0;

        // Case C1: Entry Points only
        if (hasMultiplierEntryPoints && !hasMultiplierRewardPoints) {
          const entryText =
            validWLPromo.entryPoints === 1
              ? "ANOTHER"
              : validWLPromo.entryPoints;
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU <br />
                <span className={s.pointsHighlight}>
                  {entryText} MONTH'S WORTH OF ENTRIES
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C2: Locker Points only
        if (hasMultiplierRewardPoints && !hasMultiplierEntryPoints) {
          const rewardText =
            validWLPromo.rewardPoints === 1
              ? "ANOTHER"
              : validWLPromo.rewardPoints;
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU <br />
                <span className={s.pointsHighlight}>
                  {rewardText} MONTH'S WORTH OF LOCKER POINTS
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C3: Entry + Locker points
        if (hasMultiplierEntryPoints && hasMultiplierRewardPoints) {
          const entryText =
            validWLPromo.entryPoints === 1
              ? "ANOTHER"
              : validWLPromo.entryPoints;
          const rewardText =
            validWLPromo.rewardPoints === 1
              ? "ANOTHER"
              : validWLPromo.rewardPoints;
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU <br />
                <span className={s.pointsHighlight}>
                  {entryText} MONTH'S WORTH OF ENTRIES AND {rewardText} MONTH'S
                  WORTH OF LOCKER POINTS
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C4: Entry Points Month's Worth + Fixed Amount Discount
        if (
          hasMultiplierEntryPoints &&
          !hasMultiplierRewardPoints &&
          hasFixedDiscount
        ) {
          const entryText =
            validWLPromo.entryPoints === 1
              ? "ANOTHER"
              : validWLPromo.entryPoints;
          const discountAmount = validWLPromo.stripeCreditDiscount || 0;

          const finalPrice = Math.max(
            1,
            (selectedProduct.price || 0) - discountAmount
          );
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU <br />
                <span className={s.pointsHighlight}>
                  {entryText} MONTH'S WORTH OF ENTRIES + PAY ONLY ${finalPrice}
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C5: Locker Points Month's Worth + Fixed Amount Discount
        if (
          hasMultiplierRewardPoints &&
          !hasMultiplierEntryPoints &&
          hasFixedDiscount
        ) {
          const rewardText =
            validWLPromo.rewardPoints === 1
              ? "ANOTHER"
              : validWLPromo.rewardPoints;
          const discountAmount = validWLPromo.stripeCreditDiscount || 0;

          const finalPrice = Math.max(
            1,
            (selectedProduct.price || 0) - discountAmount
          );
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU
                <br />
                <span className={s.pointsHighlight}>
                  {rewardText} MONTH'S WORTH OF LOCKER POINTS + PAY ONLY $
                  {finalPrice}
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C6: Entry Month's Worth + Locker Month's Worth + Fixed Amount Discount
        if (
          hasMultiplierEntryPoints &&
          hasMultiplierRewardPoints &&
          hasFixedDiscount
        ) {
          const entryText =
            validWLPromo.entryPoints === 1
              ? "ANOTHER"
              : validWLPromo.entryPoints;
          const rewardText =
            validWLPromo.rewardPoints === 1
              ? "ANOTHER"
              : validWLPromo.rewardPoints;
          const discountAmount = validWLPromo.stripeCreditDiscount || 0;

          const finalPrice = Math.max(
            1,
            (selectedProduct.price || 0) - discountAmount
          );
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU <br />
                <span className={s.pointsHighlight}>
                  {entryText} MONTH'S WORTH OF ENTRIES + {rewardText} MONTH'S
                  WORTH OF LOCKER POINTS + PAY ONLY ${finalPrice}
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C7: Entry Points Month's Worth + Percentage Discount
        if (
          hasMultiplierEntryPoints &&
          !hasMultiplierRewardPoints &&
          hasPercentageDiscount
        ) {
          const entryText =
            validWLPromo.entryPoints === 1
              ? "ANOTHER"
              : validWLPromo.entryPoints;
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU
                <br />
                <span className={s.pointsHighlight}>
                  {entryText} MONTH'S WORTH OF ENTRIES + GET{" "}
                  {validWLPromo.stripeCreditDiscount}% OFF
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C8: Locker Points Month's Worth + Percentage Discount
        if (
          hasMultiplierRewardPoints &&
          !hasMultiplierEntryPoints &&
          hasPercentageDiscount
        ) {
          const rewardText =
            validWLPromo.rewardPoints === 1
              ? "ANOTHER"
              : validWLPromo.rewardPoints;
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU
                <br />
                <span className={s.pointsHighlight}>
                  {rewardText} MONTH'S WORTH OF LOCKER POINTS + GET{" "}
                  {validWLPromo.stripeCreditDiscount}% OFF
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }

        // Case C9: Entry Month's Worth + Locker Month's Worth + Percentage Discount
        if (
          hasMultiplierEntryPoints &&
          hasMultiplierRewardPoints &&
          hasPercentageDiscount
        ) {
          const entryText =
            validWLPromo.entryPoints === 1
              ? "ANOTHER"
              : validWLPromo.entryPoints;
          const rewardText =
            validWLPromo.rewardPoints === 1
              ? "ANOTHER"
              : validWLPromo.rewardPoints;
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO JUICE UP THE OFFER, WE'LL GIVE YOU
                <br />
                <span className={s.pointsHighlight}>
                  {entryText} MONTH'S WORTH OF ENTRIES + {rewardText} MONTH'S
                  WORTH OF LOCKER POINTS + GET{" "}
                  {validWLPromo.stripeCreditDiscount}% OFF
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }
        // Case10 - if has %OFF only
        if (hasPercentageDiscount && !hasRewardPoints && !hasEntryPoints) {
          return (
            <>
              <IonLabel className={s.bonusText}>
                TO ADD A BIT OF SAUCE, WE'LL MAKE IT
                <span className={s.pointsHighlight}>
                  {" "}
                  {validWLPromo.stripeCreditDiscount}% off
                </span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }
        // case C11: if has Discount $ only
        if (hasFixedDiscount && !hasRewardPoints && !hasEntryPoints) {
          const discountAmount = validWLPromo.stripeCreditDiscount || 0;

          const finalPrice = Math.max(
            1,
            (selectedProduct.price || 0) - discountAmount
          );

          return (
            <>
              <IonLabel className={s.bonusText}>
                MAKE PAYMENT FOR{" "}
                <span className={s.pointsHighlight}>${finalPrice}</span>
              </IonLabel>
              <IonLabel className={s.completeNow}>
                IF YOU FIX YOUR PAYMENT NOW
              </IonLabel>
            </>
          );
        }
      }
    }

    // Default fallback
    return <></>;
  }, [selectedProduct, validWLPromo, user]);

  return (
    <div className={cx(s.currentEntries, { [s.mobile]: isMobile })}>
      <div className={s.content}>
        <div className={s.header}>{renderMainTitle}</div>
        <div className={s.entriesBox}>
          <div className={s.safeIcon}>
            <img
              src={fixbillingLocker}
              alt="Safe with entries"
              className={s.lockerImage}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className={s.entriesInfo}>
            <IonLabel className={s.entriesText}>
              YOUR {userEntries}{" "}
              {formatWordPluralize("ENTRY", userEntries || 0)}
            </IonLabel>
            <IonLabel className={s.areOnTheLine}>ARE ON THE LINE!</IonLabel>
          </div>
        </div>

        <div className={s.bonusOffer}>{renderPromoSpiel}</div>
      </div>
    </div>
  );
};

export default memo(CurrentEntries);
